import React, { useCallback, useEffect, useRef } from "react";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/shadcn";
import "@blocknote/core/fonts/inter.css";
import "@blocknote/shadcn/style.css";
import { useTheme } from "../../contexts/ThemeContext";
import { Button } from "./button";
import { Badge } from "./badge";
import { Card, CardContent } from "./card";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { Input } from "./input";
import { Label } from "./label";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { Skeleton } from "./skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./tabs";
import { Toggle } from "./toggle";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";

// BlockNote renders its side-menu buttons (the "+" and drag handle) using the
// Button we pass here. A native <button> inside a <form> defaults to
// type="submit", so without this override clicking "+" would submit the
// surrounding form. Force type="button" to keep those clicks editor-only.
const EditorButton = React.forwardRef(({ type, ...props }, ref) => (
  <Button ref={ref} type={type ?? "button"} {...props} />
));
EditorButton.displayName = "EditorButton";

const shadCNComponents = {
  Avatar: { Avatar, AvatarFallback, AvatarImage },
  Badge: { Badge },
  Button: { Button: EditorButton },
  Card: { Card, CardContent },
  DropdownMenu: {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
  },
  Input: { Input },
  Label: { Label },
  Popover: { Popover, PopoverContent, PopoverTrigger },
  Select: { Select, SelectContent, SelectItem, SelectTrigger, SelectValue },
  Skeleton: { Skeleton },
  Tabs: { Tabs, TabsContent, TabsList, TabsTrigger },
  Toggle: { Toggle },
  Tooltip: { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger },
};

const RichTextEditor = ({ value = "", onChange, placeholder = "Start writing..." }) => {
  const { theme } = useTheme();
  const lastSyncedHtml = useRef(null);
  const isInternalUpdate = useRef(false);
  const debounceTimer = useRef(null);

  const editor = useCreateBlockNote(
    {
      placeholders: {
        default: placeholder,
      },
    },
    []
  );

  const syncHtmlToEditor = useCallback(
    (html) => {
      if (!editor) return;

      const normalizedHtml = html || "";
      const blocks = editor.tryParseHTMLToBlocks(
        normalizedHtml.trim() ? normalizedHtml : "<p></p>"
      );
      editor.replaceBlocks(editor.document, blocks);

      lastSyncedHtml.current = normalizedHtml;
    },
    [editor]
  );

  useEffect(() => {
    if (!editor) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    if (value === lastSyncedHtml.current) return;

    syncHtmlToEditor(value);
  }, [editor, value, syncHtmlToEditor]);

  const handleChange = useCallback(() => {
    if (!editor || !onChange) return;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      const html = editor.blocksToHTMLLossy();
      isInternalUpdate.current = true;
      lastSyncedHtml.current = html;
      onChange(html);
    }, 300);
  }, [editor, onChange]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  if (!editor) {
    return (
      <div className="min-h-[300px] rounded-md border border-input bg-background p-4 text-sm text-muted-foreground">
        Loading editor...
      </div>
    );
  }

  return (
    <div className="rich-text-editor min-h-[300px] overflow-hidden rounded-md border border-input bg-background">
      <BlockNoteView
        editor={editor}
        theme={theme}
        onChange={handleChange}
        shadCNComponents={shadCNComponents}
      />
    </div>
  );
};

export default RichTextEditor;
